<template>
  <div class="home-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-bg-shapes">
        <div class="shape shape-1"></div>
        <div class="shape shape-2"></div>
        <div class="shape shape-3"></div>
      </div>
      <div class="hero-content">
        <div class="hero-badge">🚀 高效刷题，智能组卷</div>
        <h1 class="hero-title">
          <span class="highlight">刷题网</span> — 你的数学备考加速器
        </h1>
        <p class="hero-subtitle">
          海量真题试卷 · AI 智能组卷 · 精选专题题单 · 社区互助学习
        </p>
        <div class="hero-actions">
          <el-button type="primary" size="large" class="hero-btn-primary" @click="$router.push('/zujuan')">
            <el-icon>
              <EditPen />
            </el-icon> 开始组卷
          </el-button>
          <el-button size="large" class="hero-btn-secondary" @click="$router.push('/papers')">
            <el-icon>
              <Files />
            </el-icon> 浏览试卷库
          </el-button>
        </div>
        <div class="hero-stats">
          <div class="stat-pill">
            <span class="stat-number">{{ statsData.questionCount || '1000+' }}</span>
            <span class="stat-desc">精选试题</span>
          </div>
          <div class="stat-pill">
            <span class="stat-number">{{ statsData.paperCount || '200+' }}</span>
            <span class="stat-desc">真题试卷</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Feature Cards -->
    <section class="features-section">
      <div class="section-container">
        <div class="features-grid">
          <div v-for="feature in features" :key="feature.title" class="feature-card"
            @click="$router.push(feature.route)">
            <div class="feature-icon" :style="{ background: feature.gradient }">
              <span class="feature-emoji">{{ feature.emoji }}</span>
            </div>
            <h3 class="feature-title">{{ feature.title }}</h3>
            <p class="feature-desc">{{ feature.desc }}</p>
            <div class="feature-link">
              进入 <el-icon>
                <ArrowRight />
              </el-icon>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Content Section -->
    <section class="content-section">
      <div class="section-container">
        <el-row :gutter="28">
          <!-- Left: Recent Papers -->
          <el-col :span="16">
            <div class="content-block">
              <div class="block-header">
                <div class="block-title">
                  <el-icon class="block-icon">
                    <Document />
                  </el-icon>
                  <span>最新试卷</span>
                </div>
                <el-button text type="primary" @click="$router.push('/papers')">查看全部 →</el-button>
              </div>
              <div v-if="loadingPapers" class="loading-placeholder">
                <el-skeleton :rows="4" animated />
              </div>
              <div v-else-if="recentPapers.length === 0" class="empty-state">
                <el-empty description="暂无试卷" :image-size="80" />
              </div>
              <div v-else class="paper-list">
                <div v-for="paper in recentPapers" :key="paper.id" class="paper-item"
                  @click="$router.push(`/papers/${paper.id}`)">
                  <div class="paper-icon-box">
                    <el-icon>
                      <Files />
                    </el-icon>
                  </div>
                  <div class="paper-info">
                    <div class="paper-title">{{ paper.name }}</div>
                    <div class="paper-meta">
                      <span class="meta-date">{{ formatDate(paper.createdAt) }}</span>
                      <el-tag v-if="paper.source" size="small" type="info" effect="plain">{{ paper.source }}</el-tag>
                    </div>
                  </div>
                  <el-icon class="paper-arrow">
                    <ArrowRight />
                  </el-icon>
                </div>
              </div>
            </div>

            <!-- Hot Papers -->
            <div class="content-block" style="margin-top: 24px;">
              <div class="block-header">
                <div class="block-title">
                  <el-icon class="block-icon">
                    <Files />
                  </el-icon>
                  <span>热门试卷</span>
                </div>
                <el-button text type="primary" @click="$router.push('/papers')">去下载 →</el-button>
              </div>
              <div v-if="loadingHotPapers" class="loading-placeholder">
                <el-skeleton :rows="3" animated />
              </div>
              <div v-else class="hot-list">
                <div v-for="paper in hotPapers" :key="paper.id" class="hot-item"
                  @click="$router.push(`/papers/${paper.id}`)">
                  <span class="hot-tag">HOT</span>
                  <span class="hot-name">{{ paper.name }}</span>
                  <span class="hot-count">{{ paper.downloadCount }} 下载</span>
                </div>
              </div>
            </div>

            <!-- Categorized Question Lists -->
            <div class="content-block" style="margin-top: 24px;">
              <div class="block-header">
                <div class="block-title">
                  <el-icon class="block-icon">
                    <Collection />
                  </el-icon>
                  <span>精选题单</span>
                </div>
                <el-button text type="primary" @click="$router.push('/question-lists')">看全部 →</el-button>
              </div>

              <div v-if="loadingCategories" class="loading-placeholder">
                <el-skeleton :rows="3" animated />
              </div>

              <div v-else class="categorized-lists">
                <!-- 1. Official Featured -->
                <div class="category-section" v-if="categorizedLists.official?.length">
                  <div class="category-title">
                    <el-icon>
                      <Trophy />
                    </el-icon> 官方精选
                  </div>
                  <div class="hot-list">
                    <div v-for="list in categorizedLists.official" :key="list.id" class="hot-item list-theme official"
                      @click="$router.push(`/question-lists/${list.id}`)">
                      <span class="hot-tag">精选</span>
                      <span class="hot-name">{{ list.title }}</span>
                      <span class="hot-count">{{ list.viewCount }} 阅览</span>
                    </div>
                  </div>
                </div>

                <!-- 2. User Shared -->
                <div class="category-section" v-if="categorizedLists.shared?.length">
                  <div class="category-title">
                    <el-icon>
                      <Share />
                    </el-icon> 用户分享
                  </div>
                  <div class="hot-list">
                    <div v-for="list in categorizedLists.shared" :key="list.id" class="hot-item list-theme"
                      @click="$router.push(`/question-lists/${list.id}`)">
                      <span class="hot-tag">热门</span>
                      <span class="hot-name">{{ list.title }}</span>
                      <span class="hot-count">{{ list.viewCount }} 阅览</span>
                    </div>
                  </div>
                </div>

                <!-- 3. My Collection (Only if logged in) -->
                <div class="category-section" v-if="categorizedLists.mine?.length">
                  <div class="category-title">
                    <el-icon>
                      <StarFilled />
                    </el-icon> 我的收藏
                  </div>
                  <div class="hot-list">
                    <div v-for="list in categorizedLists.mine" :key="list.id" class="hot-item list-theme mine"
                      @click="$router.push(`/question-lists/${list.id}`)">
                      <span class="hot-tag" :style="list.isDefault ? 'background:#e6f7ff; color:#1890ff;' : ''">{{
                        list.isDefault ? '默认' : '我的' }}</span>
                      <span class="hot-name">{{ list.title }}</span>
                      <span class="hot-count">{{ list.viewCount }} 阅览</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </el-col>

          <!-- Right: Sidebar -->
          <el-col :span="8">
            <!-- Announcements -->
            <div class="content-block sidebar-block">
              <div class="block-header">
                <div class="block-title">
                  <el-icon class="block-icon">
                    <Bell />
                  </el-icon>
                  <span>系统公告</span>
                </div>
              </div>
              <div class="announcement-list">
                <div v-for="(notice, index) in announcements" :key="notice.id || index" class="notice-item"
                  @click="viewNotice(notice)">
                  <el-tag size="small" :type="notice.tagType || 'info'" effect="dark" class="notice-tag" round>{{
                    notice.tag
                  }}</el-tag>
                  <span class="notice-text">{{ notice.title }}</span>
                </div>
              </div>
            </div>

            <!-- Editor's Picks (Featured) -->
            <div class="content-block sidebar-block" style="margin-top: 24px;">
              <div class="block-header">
                <div class="block-title">
                  <el-icon class="block-icon">
                    <Star />
                  </el-icon>
                  <span>编辑精选</span>
                </div>
              </div>
              <div v-if="loadingFeatured" class="loading-placeholder">
                <el-skeleton :rows="3" animated />
              </div>
              <div v-else class="featured-list">
                <div v-for="item in featuredPapers" :key="'p' + item.id" class="featured-item"
                  @click="$router.push(`/papers/${item.id}`)">
                  <div class="featured-type">试卷</div>
                  <div class="featured-text">{{ item.name }}</div>
                </div>
                <div v-for="item in featuredLists" :key="'l' + item.id" class="featured-item"
                  @click="$router.push(`/question-lists/${item.id}`)">
                  <div class="featured-type list-type">题单</div>
                  <div class="featured-text">{{ item.title }}</div>
                </div>
              </div>
            </div>
          </el-col>
        </el-row>
      </div>
    </section>

    <!-- Notice Detail Dialog -->
    <el-dialog v-model="showNoticeDialog" :title="selectedNotice?.title" width="600px" destroy-on-close align-center
      class="notice-dialog">
      <div v-if="selectedNotice" class="notice-detail">
        <div class="notice-detail-header">
          <el-tag size="small" :type="selectedNotice.tagType || 'info'" effect="dark" round>
            {{ selectedNotice.tag }}
          </el-tag>
          <span class="notice-detail-date">{{ formatDate(selectedNotice.createdAt) }}</span>
        </div>
        <div class="notice-detail-content">
          <md-editor v-model="selectedNotice.content" preview-only style="text-align: left" />
        </div>
      </div>
    </el-dialog>

    <!-- Footer -->
    <footer class="site-footer">
      <div class="footer-content">
        <span>© 2026 刷题网 — 高效刷题，助力成长</span>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/utils/api'
import {
  Document, Files, ArrowRight, Collection, Bell, ChatLineRound, EditPen, Star, StarFilled, Trophy, Share
} from '@element-plus/icons-vue'
import MdEditor from 'md-editor-v3'
import 'md-editor-v3/lib/style.css'
import dayjs from 'dayjs'

const router = useRouter()

const recentPapers = ref([])
const loadingPapers = ref(true)

const hotPapers = ref([])
const loadingHotPapers = ref(true)

const categorizedLists = ref({ official: [], shared: [], mine: [] })
const loadingCategories = ref(true)

const featuredPapers = ref([])
const featuredLists = ref([])
const loadingFeatured = ref(true)

const showNoticeDialog = ref(false)
const selectedNotice = ref(null)

const statsData = ref({})

const features = ref([
  {
    emoji: '✏️', title: '智能组卷', desc: '从千题库中自由挑选，一键生成 Word 试卷',
    route: '/zujuan', gradient: 'linear-gradient(135deg, #dbeafe, #bfdbfe)'
  },
  {
    emoji: '📄', title: '试卷资源', desc: '海量真题、模拟卷，在线预览与下载',
    route: '/papers', gradient: 'linear-gradient(135deg, #fce7f3, #fbcfe8)'
  },
  {
    emoji: '📋', title: '精选题单', desc: '名师精选专题训练，按考点高效刷题',
    route: '/question-lists', gradient: 'linear-gradient(135deg, #d1fae5, #a7f3d0)'
  },
  {
    emoji: '💬', title: '学习论坛', desc: '问答交流、经验分享，师生互动社区',
    route: '/forum', gradient: 'linear-gradient(135deg, #fef3c7, #fde68a)'
  },
  {
    emoji: '🏆', title: '活动课程', desc: '精选专题课程、精品试卷与专项训练',
    route: '/activities', gradient: 'linear-gradient(135deg, #ede9fe, #ddd6fe)'
  }
])

const topics = ref([
  { id: 1, name: '函数与导数', count: 12, icon: '📈' },
  { id: 2, name: '立体几何', count: 8, icon: '📐' },
  { id: 3, name: '解析几何', count: 15, icon: '🎯' },
  { id: 4, name: '概率统计', count: 6, icon: '🎲' },
  { id: 5, name: '数列', count: 9, icon: '🔢' },
  { id: 6, name: '三角函数', count: 11, icon: '📊' },
])

const announcements = ref([])

const fetchAnnouncements = async () => {
  try {
    const res = await api.get('/api/announcements/published')
    announcements.value = res.data?.announcements || []
  } catch (e) {
    // Fallback to empty
  }
}

const fetchRecentPapers = async () => {
  try {
    const res = await api.get('/api/papers', { params: { page: 1, pageSize: 5 } })
    if (res.data?.papers) recentPapers.value = res.data.papers
  } catch (e) {
    console.error('Failed to fetch papers:', e)
  } finally {
    loadingPapers.value = false
  }
}

const fetchHotPapers = async () => {
  try {
    const res = await api.get('/api/papers/hot')
    if (res.data?.papers) hotPapers.value = res.data.papers
  } catch (e) {
    console.error('Failed to fetch hot papers:', e)
  } finally {
    loadingHotPapers.value = false
  }
}

const fetchCategorizedLists = async () => {
  try {
    const res = await api.get('/api/question-lists/categorized')
    if (res.data) categorizedLists.value = res.data
  } catch (e) {
    console.error('Failed to fetch categorized lists:', e)
  } finally {
    loadingCategories.value = false
  }
}

const fetchFeaturedItems = async () => {
  try {
    const res = await api.get('/api/featured')
    if (res.data) {
      featuredPapers.value = res.data.papers || []
      featuredLists.value = res.data.questionLists || []
    }
  } catch (e) {
    console.error('Failed to fetch featured items:', e)
  } finally {
    loadingFeatured.value = false
  }
}

const fetchStats = async () => {
  try {
    const res = await api.get('/api/public-stats')
    if (res.data) statsData.value = res.data
  } catch (e) {
    // Non-critical, use defaults
  }
}

const formatDate = (ts) => {
  if (!ts) return ''
  // Handle unix timestamp (int64) or ISO string
  if (typeof ts === 'number') return dayjs.unix(ts).format('YYYY-MM-DD')
  return dayjs(ts).format('YYYY-MM-DD')
}

const renderMath = (content) => {
  if (!content) return ''
  let text = content.replace(/<[^>]+>/g, '')
  if (text.length > 40) text = text.substring(0, 40) + '...'
  return text
}

const viewNotice = (notice) => {
  selectedNotice.value = notice
  showNoticeDialog.value = true
}

const goToTopic = (topicName) => {
  router.push({ path: '/papers', query: { search: topicName } })
}

onMounted(() => {
  fetchRecentPapers()
  fetchHotPapers()
  fetchCategorizedLists()
  fetchFeaturedItems()
  fetchAnnouncements()
  fetchStats()
})
</script>

<style scoped>
.home-page {
  min-height: 100vh;
  background: #f8fafc;
}

/* ============ Hero ============ */
.hero-section {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #1e3a8a 0%, #2563eb 50%, #3b82f6 100%);
  padding: 80px 20px 60px;
  text-align: center;
  color: white;
}

.hero-bg-shapes {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.shape {
  position: absolute;
  border-radius: 50%;
  opacity: 0.08;
  background: white;
}

.shape-1 {
  width: 500px;
  height: 500px;
  top: -200px;
  right: -100px;
  animation: float 20s ease-in-out infinite;
}

.shape-2 {
  width: 300px;
  height: 300px;
  bottom: -100px;
  left: -50px;
  animation: float 15s ease-in-out infinite reverse;
}

.shape-3 {
  width: 200px;
  height: 200px;
  top: 50%;
  left: 60%;
  animation: float 12s ease-in-out infinite;
}

@keyframes float {

  0%,
  100% {
    transform: translateY(0) scale(1);
  }

  50% {
    transform: translateY(-30px) scale(1.05);
  }
}

.hero-content {
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
}

.hero-badge {
  display: inline-block;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  padding: 6px 20px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 24px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.hero-title {
  font-size: 44px;
  font-weight: 900;
  line-height: 1.2;
  margin: 0 0 20px;
  letter-spacing: -0.03em;
}

.hero-title .highlight {
  background: linear-gradient(120deg, #fbbf24, #f59e0b);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 18px;
  color: #bfdbfe;
  margin-bottom: 36px;
  line-height: 1.6;
}

.hero-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-bottom: 48px;
}

.hero-btn-primary {
  height: 52px;
  padding: 0 32px;
  font-size: 16px;
  font-weight: 700;
  border-radius: 14px;
  background: #ffffff !important;
  color: #2563eb !important;
  border: none !important;
  box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.3);
  transition: all 0.3s;
}

.hero-btn-primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 40px -15px rgba(0, 0, 0, 0.4);
}

.hero-btn-secondary {
  height: 52px;
  padding: 0 32px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(10px);
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.25) !important;
  transition: all 0.3s;
}

.hero-btn-secondary:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  border-color: rgba(255, 255, 255, 0.4) !important;
  transform: translateY(-2px);
}

.hero-stats {
  display: flex;
  justify-content: center;
  gap: 32px;
}

.stat-pill {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.stat-number {
  font-size: 28px;
  font-weight: 900;
}

.stat-desc {
  font-size: 13px;
  color: #93c5fd;
  font-weight: 500;
}

/* ============ Features ============ */
.section-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 32px;
}

@media (max-width: 1440px) {
  .section-container {
    max-width: 100%;
    padding: 0 24px;
  }
}

.features-section {
  margin-top: -30px;
  position: relative;
  z-index: 2;
  padding-bottom: 16px;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
}

.feature-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 28px 24px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #f1f5f9;
  box-shadow: 0 4px 20px -5px rgba(0, 0, 0, 0.06);
}

.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.1);
}

.feature-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
}

.feature-emoji {
  font-size: 28px;
}

.feature-title {
  font-size: 18px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 8px;
}

.feature-desc {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 16px;
}

.feature-link {
  font-size: 13px;
  font-weight: 700;
  color: #3b82f6;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: gap 0.2s;
}

.feature-card:hover .feature-link {
  gap: 8px;
}

/* ============ Content ============ */
.content-section {
  padding: 32px 0 60px;
}

.content-block {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #f1f5f9;
}

.sidebar-block {
  border-radius: 16px;
}

.block-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.block-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 17px;
  font-weight: 800;
  color: #1e293b;
}

.block-icon {
  color: #3b82f6;
  font-size: 18px;
}

/* Paper List */
.paper-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.paper-item {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.25s;
  background: #fafbfc;
}

.paper-item:hover {
  background: #eff6ff;
  transform: translateX(4px);
}

.paper-icon-box {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  margin-right: 14px;
  flex-shrink: 0;
}

.paper-info {
  flex: 1;
  min-width: 0;
}

.paper-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.paper-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.meta-date {
  color: #94a3b8;
}

.paper-arrow {
  color: #cbd5e1;
  flex-shrink: 0;
}

/* Hot List (Papers & Question Lists) */
.hot-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.hot-item {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: all 0.2s;
}

.hot-item:last-child {
  border-bottom: none;
}

.hot-item:hover {
  transform: translateX(6px);
}

.hot-tag {
  background: #fee2e2;
  color: #ef4444;
  font-size: 10px;
  font-weight: 800;
  padding: 2px 6px;
  border-radius: 4px;
  margin-right: 12px;
  flex-shrink: 0;
}

.hot-item.list-theme .hot-tag {
  background: #dcfce7;
  color: #22c55e;
}

.hot-name {
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hot-count {
  font-size: 12px;
  color: #94a3b8;
  margin-left: 12px;
}

/* Featured List (Editor's Picks) */
.featured-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.featured-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid transparent;
}

.featured-item:hover {
  background: #ffffff;
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
}

.featured-type {
  font-size: 11px;
  font-weight: 700;
  color: #3b82f6;
  background: #eff6ff;
  padding: 2px 8px;
  border-radius: 6px;
  flex-shrink: 0;
}

.featured-type.list-type {
  color: #8b5cf6;
  background: #f5f3ff;
}

.hot-item.list-theme.official {
  border-left: 3px solid #f59e0b;
  background: #fffbeb;
}

.hot-item.list-theme.mine {
  border-left: 3px solid #10b981;
  background: #ecfdf5;
}

.category-section {
  margin-bottom: 20px;
}

.category-section:last-child {
  margin-bottom: 0;
}

.category-title {
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.featured-text {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Announcements */
.announcement-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.notice-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s;
}

.notice-item:hover {
  background: #f1f5f9;
  transform: translateX(4px);
}

.notice-item:hover .notice-text {
  color: #3b82f6;
}

.notice-tag {
  flex-shrink: 0;
  font-size: 11px;
}

.notice-text {
  font-size: 13px;
  color: #475569;
  line-height: 1.6;
  transition: color 0.2s;
}

/* Notice Dialog */
.notice-dialog :deep(.el-dialog__body) {
  padding-top: 10px;
}

.notice-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
}

.notice-detail-date {
  font-size: 12px;
  color: #94a3b8;
}

.notice-detail-content {
  max-height: 400px;
  overflow-y: auto;
}

.empty-content {
  padding: 40px 0;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
}

/* Footer */
.site-footer {
  padding: 40px 0;
  text-align: center;
  border-top: 1px solid #f1f5f9;
  margin-top: 60px;
}

.footer-content {
  font-size: 14px;
  color: #94a3b8;
}

/* Responsive */
@media (max-width: 768px) {
  .hero-title {
    font-size: 28px;
  }

  .features-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .topic-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .hero-actions {
    flex-direction: column;
    align-items: center;
  }

  .hero-stats {
    gap: 20px;
  }
}
</style>
